import React, { useState, useEffect, useCallback } from 'react';
import { defectsApi, settingsApi } from '../../services/api';
import type { Defect, DefectStatus, DefectSeverity } from '../../types';

// ─── Config ───────────────────────────────────────────────────────────────────

const STATUS_CFG: Record<string, { label: string; color: string; bg: string; icon: string }> = {
  open:             { label: 'Abierto',         color: '#A32D2D', bg: '#FCEBEB', icon: '○' },
  in_progress:      { label: 'En progreso',      color: '#185FA5', bg: '#E6F1FB', icon: '◉' },
  not_reproducible: { label: 'No reproducible',  color: '#854F0B', bg: '#FAEEDA', icon: '?' },
  rejected:         { label: 'Rechazado',        color: '#5F5E5A', bg: '#F1EFE8', icon: '✕' },
  resolved:         { label: 'Resuelto',         color: '#0F6E56', bg: '#E1F5EE', icon: '✓' },
  closed:           { label: 'Cerrado',          color: '#3B5FAD', bg: '#EBF0FC', icon: '✓✓' },
  wont_fix:         { label: 'No se corregirá',  color: '#7A4515', bg: '#FEF2E7', icon: '⊘' },
};

const SEVERITY_CFG: Record<string, { label: string; color: string; bg: string }> = {
  critical: { label: 'Crítica', color: '#7A1515', bg: '#FCEBEB' },
  high:     { label: 'Alta',    color: '#7A4515', bg: '#FEF2E7' },
  medium:   { label: 'Media',   color: '#3B5FAD', bg: '#EBF0FC' },
  low:      { label: 'Baja',    color: '#0F6E56', bg: '#E1F5EE' },
};

const PRIORITY_LABEL: Record<string, string> = { p1: 'P1 — Crítica', p2: 'P2 — Alta', p3: 'P3 — Normal', p4: 'P4 — Baja' };

const TRANSITIONS: Record<string, string[]> = {
  open:             ['in_progress', 'wont_fix'],
  in_progress:      ['not_reproducible', 'rejected', 'resolved', 'open'],
  not_reproducible: ['open', 'closed'],
  rejected:         ['open', 'closed'],
  resolved:         ['closed', 'open'],
  closed:           ['open'],
  wont_fix:         ['open'],
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso?: string | null) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('es', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function StatusBadge({ status }: { status: string }) {
  const cfg = STATUS_CFG[status] ?? STATUS_CFG.open;
  return <span style={{ padding: '2px 8px', borderRadius: 5, background: cfg.bg, color: cfg.color, fontSize: 10, fontWeight: 700 }}>{cfg.icon} {cfg.label}</span>;
}

function SeverityBadge({ severity }: { severity: string }) {
  const cfg = SEVERITY_CFG[severity] ?? SEVERITY_CFG.medium;
  return <span style={{ padding: '1px 7px', borderRadius: 5, background: cfg.bg, color: cfg.color, fontSize: 10, fontWeight: 700 }}>{cfg.label}</span>;
}

const INPUT: React.CSSProperties = { width: '100%', padding: '8px 10px', fontSize: 13, borderRadius: 7, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)', outline: 'none', boxSizing: 'border-box' };

// ─── DefectDetailPanel ────────────────────────────────────────────────────────

function DefectDetailPanel({ defect, onClose, onUpdated }: {
  defect: Defect; onClose: () => void; onUpdated: (d: Defect) => void;
}) {
  const [statusModal, setStatusModal] = useState<string | null>(null);
  const [comment, setComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<{ id: string; full_name: string; email: string }[]>([]);
  const [assignSaving, setAssignSaving] = useState(false);
  const [jiraBusy, setJiraBusy] = useState(false);

  useEffect(() => {
    settingsApi.listUsers().then(u =>
      setUsers((u as { id: string; full_name: string; email: string }[]).filter(x => x.id))
    ).catch(() => {});
  }, []);

  const handleAssign = async (userId: string) => {
    setAssignSaving(true);
    try {
      const updated = await defectsApi.update(defect.id, { assigned_to: userId || undefined });
      onUpdated(updated);
    } catch (e) { console.error(e); }
    finally { setAssignSaving(false); }
  };

  const handleStatusChange = async (newStatus: string) => {
    setSaving(true);
    try {
      const updated = await defectsApi.changeStatus(defect.id, newStatus, comment || undefined);
      onUpdated(updated);
      setStatusModal(null);
      setComment('');
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleReportToJira = async () => {
    setJiraBusy(true);
    try {
      const updated = await defectsApi.reportToJira(defect.id);
      onUpdated(updated);
    } catch (e) { console.error(e); alert((e as Error).message); }
    finally { setJiraBusy(false); }
  };

  const handleSyncJira = async () => {
    setJiraBusy(true);
    try {
      const updated = await defectsApi.syncJiraStatus(defect.id);
      onUpdated(updated);
    } catch (e) { console.error(e); alert((e as Error).message); }
    finally { setJiraBusy(false); }
  };

  const allowed = TRANSITIONS[defect.status] ?? [];
  const sc = STATUS_CFG[defect.status] ?? STATUS_CFG.open;
  const sev = SEVERITY_CFG[defect.severity] ?? SEVERITY_CFG.medium;

  return (
    <div style={{ width: 440, borderLeft: '1px solid var(--color-border-tertiary)', display: 'flex', flexDirection: 'column', height: '100%', background: 'var(--color-background-primary)' }}>
      {/* Header */}
      <div style={{ padding: '16px 18px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--color-text-primary)', marginBottom: 6 }}>{defect.title}</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <StatusBadge status={defect.status} />
            <SeverityBadge severity={defect.severity} />
            <span style={{ padding: '1px 7px', borderRadius: 5, background: 'var(--color-background-secondary)', color: 'var(--color-text-tertiary)', fontSize: 10, fontWeight: 600 }}>{PRIORITY_LABEL[defect.priority] ?? defect.priority}</span>
            {defect.jira_issue_key && (
              <span style={{ padding: '1px 7px', borderRadius: 5, background: '#E6F1FB', color: '#185FA5', fontSize: 10, fontWeight: 700 }}>
                🔵 {defect.jira_issue_key}{defect.jira_status ? ` · ${defect.jira_status}` : ''}
              </span>
            )}
          </div>
        </div>
        <button onClick={onClose} style={{ border: 'none', background: 'transparent', cursor: 'pointer', fontSize: 16, color: 'var(--color-text-tertiary)', padding: 4 }}>✕</button>
      </div>

      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 14 }}>
        {/* Metadata */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11 }}>
          {[
            ['Caso', defect.test_case_title || '—'],
            ['Ambiente', defect.environment || '—'],
            ['Creado', formatDate(defect.created_at)],
            ['Resuelto', formatDate(defect.resolved_at)],
          ].map(([label, val]) => (
            <div key={label}>
              <div style={{ color: 'var(--color-text-tertiary)', fontWeight: 600, textTransform: 'uppercase', fontSize: 9, letterSpacing: '0.05em', marginBottom: 2 }}>{label}</div>
              <div style={{ color: 'var(--color-text-secondary)' }}>{val}</div>
            </div>
          ))}
        </div>

        {/* Asignacion */}
        <div>
          <div style={{ color: 'var(--color-text-tertiary)', fontWeight: 600, textTransform: 'uppercase', fontSize: 9, letterSpacing: '0.05em', marginBottom: 4 }}>Asignado a</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <select
              value={defect.assigned_to || ''}
              onChange={e => handleAssign(e.target.value)}
              disabled={assignSaving}
              style={{ flex: 1, padding: '6px 8px', fontSize: 12, borderRadius: 7, border: '1px solid var(--color-border-secondary)', background: 'var(--color-background-secondary)', color: 'var(--color-text-primary)', outline: 'none', opacity: assignSaving ? 0.6 : 1 }}>
              <option value=''>Sin asignar</option>
              {users.map(u => (
                <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
              ))}
            </select>
            {assignSaving && <span style={{ fontSize: 11, color: 'var(--color-text-tertiary)' }}>Guardando...</span>}
          </div>
        </div>

        {/* Description */}
        {defect.description && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: 4 }}>Descripción</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap' }}>{defect.description}</div>
          </div>
        )}

        {/* Error summary */}
        {defect.error_summary && (
          <div style={{ padding: '8px 12px', borderRadius: 8, background: '#FCEBEB', border: '1px solid #F7C1C1' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#7A1515', marginBottom: 3 }}>Error</div>
            <div style={{ fontSize: 11, color: '#7A1515', whiteSpace: 'pre-wrap' }}>{defect.error_summary}</div>
          </div>
        )}

        {/* Steps to reproduce */}
        {defect.steps_to_reproduce && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: 4 }}>Pasos para reproducir</div>
            <pre style={{ fontSize: 11, color: 'var(--color-text-secondary)', whiteSpace: 'pre-wrap', fontFamily: 'inherit', margin: 0 }}>{defect.steps_to_reproduce}</pre>
          </div>
        )}

        {/* Last status comment */}
        {defect.status_comment && (
          <div style={{ padding: '8px 12px', borderRadius: 8, background: 'var(--color-background-secondary)', border: '1px solid var(--color-border-tertiary)' }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: 3 }}>Último comentario</div>
            <div style={{ fontSize: 12, color: 'var(--color-text-secondary)' }}>{defect.status_comment}</div>
          </div>
        )}

        {/* Status history */}
        {defect.status_history.length > 0 && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', marginBottom: 8 }}>Historial</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {[...defect.status_history].reverse().map((h, i) => (
                <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <StatusBadge status={h.status} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 11, color: 'var(--color-text-secondary)' }}>{h.changed_by_name}</div>
                    {h.comment && <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', fontStyle: 'italic' }}>{h.comment}</div>}
                    <div style={{ fontSize: 9, color: 'var(--color-text-tertiary)' }}>{formatDate(h.changed_at)}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Actions — transiciones de estado */}
      {allowed.length > 0 && (
        <div style={{ padding: '12px 18px', borderTop: '1px solid var(--color-border-tertiary)' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 8 }}>Cambiar estado</div>
          {statusModal ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <textarea value={comment} onChange={e => setComment(e.target.value)}
                placeholder="Comentario (opcional)..." rows={2}
                style={{ ...INPUT, resize: 'vertical', fontSize: 12 }} />
              <div style={{ display: 'flex', gap: 6 }}>
                <button onClick={() => { setStatusModal(null); setComment(''); }}
                  style={{ flex: 1, padding: '7px 0', borderRadius: 7, border: '1px solid var(--color-border-secondary)', background: 'transparent', color: 'var(--color-text-secondary)', fontSize: 12, cursor: 'pointer' }}>
                  Cancelar
                </button>
                <button onClick={() => handleStatusChange(statusModal!)} disabled={saving}
                  style={{ flex: 1, padding: '7px 0', borderRadius: 7, border: 'none', background: '#3B8BD4', color: '#fff', fontSize: 12, fontWeight: 600, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                  {saving ? 'Guardando...' : `Confirmar → ${STATUS_CFG[statusModal]?.label}`}
                </button>
              </div>
            </div>
          ) : (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {allowed.map(s => {
                const cfg = STATUS_CFG[s];
                return (
                  <button key={s} onClick={() => setStatusModal(s)}
                    style={{ padding: '5px 10px', borderRadius: 6, border: `1px solid ${cfg.color}`, background: cfg.bg, color: cfg.color, fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                    → {cfg.label}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Jira */}
      <div style={{ padding: '12px 18px', borderTop: '1px solid var(--color-border-tertiary)' }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-text-secondary)', marginBottom: 8 }}>Jira</div>
        {defect.jira_issue_key ? (
          <button onClick={handleSyncJira} disabled={jiraBusy}
            style={{ padding: '7px 12px', borderRadius: 7, border: '1px solid var(--color-border-secondary)', background: 'transparent', color: 'var(--color-text-secondary)', fontSize: 12, cursor: jiraBusy ? 'not-allowed' : 'pointer' }}>
            {jiraBusy ? 'Sincronizando...' : '↻ Sincronizar estado de Jira'}
          </button>
        ) : (
          <button onClick={handleReportToJira} disabled={jiraBusy}
            style={{ padding: '7px 12px', borderRadius: 7, border: 'none', background: '#3B8BD4', color: '#fff', fontSize: 12, fontWeight: 600, cursor: jiraBusy ? 'not-allowed' : 'pointer' }}>
            {jiraBusy ? 'Reportando...' : '🔵 Reportar en Jira'}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Main DefectsModule ───────────────────────────────────────────────────────

export function DefectsModule() {
  const [defects, setDefects] = useState<Defect[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Defect | null>(null);
  const [filterStatus, setFilterStatus] = useState('');
  const [filterSeverity, setFilterSeverity] = useState('');
  const [qSearch, setQSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await defectsApi.list({
        status: filterStatus || undefined,
        severity: filterSeverity || undefined,
      });
      setDefects(data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [filterStatus, filterSeverity]);

  useEffect(() => { load(); }, [load]);

  const filtered = qSearch
    ? defects.filter(d => d.title.toLowerCase().includes(qSearch.toLowerCase()) || d.test_case_title?.toLowerCase().includes(qSearch.toLowerCase()))
    : defects;

  // KPIs
  const kpis = {
    open: defects.filter(d => d.status === 'open').length,
    in_progress: defects.filter(d => d.status === 'in_progress').length,
    resolved: defects.filter(d => d.status === 'resolved' || d.status === 'closed').length,
    critical: defects.filter(d => d.severity === 'critical').length,
  };

  return (
    <div style={{ display: 'flex', height: 'calc(100vh - 120px)', borderRadius: 14, border: '1px solid var(--color-border-tertiary)', overflow: 'hidden', background: 'var(--color-background-primary)' }}>
      {/* Left panel */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* KPIs */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 1, borderBottom: '1px solid var(--color-border-tertiary)', background: 'var(--color-border-tertiary)' }}>
          {[
            { label: 'Abiertos', value: kpis.open, color: '#A32D2D', bg: '#FCEBEB' },
            { label: 'En progreso', value: kpis.in_progress, color: '#185FA5', bg: '#E6F1FB' },
            { label: 'Resueltos', value: kpis.resolved, color: '#0F6E56', bg: '#E1F5EE' },
            { label: 'Críticos', value: kpis.critical, color: '#7A1515', bg: '#FCEBEB' },
          ].map(k => (
            <div key={k.label} style={{ padding: '12px 16px', background: 'var(--color-background-primary)', textAlign: 'center' }}>
              <div style={{ fontSize: 22, fontWeight: 800, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{k.label}</div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div style={{ padding: '10px 12px', borderBottom: '1px solid var(--color-border-tertiary)', display: 'flex', gap: 8 }}>
          <input value={qSearch} onChange={e => setQSearch(e.target.value)} placeholder="Buscar defecto..."
            style={{ ...INPUT, flex: 2, fontSize: 12, padding: '6px 10px' }} />
          <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
            style={{ ...INPUT, flex: 1, fontSize: 12, padding: '6px 8px' }}>
            <option value="">Todos los estados</option>
            {Object.entries(STATUS_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
          <select value={filterSeverity} onChange={e => setFilterSeverity(e.target.value)}
            style={{ ...INPUT, flex: 1, fontSize: 12, padding: '6px 8px' }}>
            <option value="">Toda severidad</option>
            {Object.entries(SEVERITY_CFG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        {/* List */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-tertiary)' }}>Cargando...</div>
          ) : filtered.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-tertiary)', fontSize: 13 }}>
              {defects.length === 0 ? '🐛 Sin defectos registrados' : 'Sin resultados con los filtros aplicados'}
            </div>
          ) : filtered.map(d => {
            const isSelected = selected?.id === d.id;
            return (
              <div key={d.id} onClick={() => setSelected(d)}
                style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border-tertiary)', cursor: 'pointer', background: isSelected ? 'var(--color-background-secondary)' : 'transparent', transition: 'background 0.1s' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text-primary)', marginBottom: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.title}</div>
                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                      <StatusBadge status={d.status} />
                      <SeverityBadge severity={d.severity} />
                      {d.test_case_title && (
                        <span style={{ fontSize: 10, color: 'var(--color-text-tertiary)', padding: '1px 6px', borderRadius: 4, background: 'var(--color-background-secondary)' }}>
                          {d.test_case_title}
                        </span>
                      )}
                    </div>
                  </div>
                  <div style={{ fontSize: 10, color: 'var(--color-text-tertiary)', flexShrink: 0 }}>{formatDate(d.created_at)}</div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Detail panel */}
      {selected && (
        <DefectDetailPanel
          defect={selected}
          onClose={() => setSelected(null)}
          onUpdated={updated => {
            setDefects(prev => prev.map(d => d.id === updated.id ? updated : d));
            setSelected(updated);
          }}
        />
      )}
    </div>
  );
}